import React from 'react';

const isRTLText = (text: string): boolean => {
  const rtlChars = /[֐-׿؀-ۿݐ-ݿࢠ-ࣿיִ-﷿ﹰ-﻿]/;
  return rtlChars.test(text);
};

interface MessageContentFormatterProps {
  content: string;
  isUserMessage: boolean;
}

const MessageContentFormatter: React.FC<MessageContentFormatterProps> = ({
  content,
  isUserMessage
}) => {
  const textColor = isUserMessage ? 'text-white' : 'text-foreground';

  const parseContent = (text: string) => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (!line) {
        elements.push(<div key={key++} className="h-2" />);
        continue;
      }

      // Main headers (Roman numerals with bold)
      if (line.match(/^\*\*[IVX]+\.\s+.*\*\*$/)) {
        const headerText = line.replace(/\*\*/g, '');
        const isRTL = isRTLText(headerText);
        elements.push(
          <p
            key={key++}
            dir={isRTL ? 'rtl' : 'ltr'}
            className={`font-semibold text-[1.1rem] mb-2 ${i > 0 ? 'mt-4' : ''} ${textColor} ${isRTL ? 'text-right' : 'text-left'}`}
          >
            {headerText}
          </p>
        );
        continue;
      }

      // Sub-headers (bold text ending with colon, or any bold)
      if (line.match(/^\*\*.*:\*\*$/) || line.match(/^\*\*.*\*\*$/)) {
        const headerText = line.replace(/\*\*/g, '');
        const isRTL = isRTLText(headerText);
        elements.push(
          <p
            key={key++}
            dir={isRTL ? 'rtl' : 'ltr'}
            className={`font-semibold text-[0.95rem] mb-1 mt-2 ${textColor} ${isRTL ? 'text-right' : 'text-left'}`}
          >
            {headerText}
          </p>
        );
        continue;
      }

      // Numbered sections (1., 2., 3., etc. with bold)
      if (line.match(/^\d+\.\s+\*\*.*\*\*$/)) {
        const sectionText = line.replace(/\*\*/g, '');
        const isRTL = isRTLText(sectionText);
        elements.push(
          <p
            key={key++}
            dir={isRTL ? 'rtl' : 'ltr'}
            className={`font-semibold text-[0.95rem] mb-1 mt-2 ${textColor} ${isRTL ? 'text-right' : 'text-left'}`}
          >
            {sectionText}
          </p>
        );
        continue;
      }

      // Bullet points
      if (line.match(/^\*\s+/) || line.match(/^-\s+/)) {
        const bulletText = line.replace(/^[\*\-\s]+/, '');
        const isRTL = isRTLText(bulletText);
        elements.push(
          <div key={key++} dir={isRTL ? 'rtl' : 'ltr'} className={`flex items-start mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className={`${textColor} text-[0.9rem] ${isRTL ? 'ml-2' : 'mr-2'} mt-px`}>•</span>
            <span dir={isRTL ? 'rtl' : 'ltr'} className={`${textColor} text-[0.9rem] leading-[1.5] flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              {bulletText}
            </span>
          </div>
        );
        continue;
      }

      // Indented sub-bullets
      if (line.match(/^\s{4,}[\*\-\s]+/)) {
        const bulletText = line.replace(/^\s{4,}[\*\-\s]+/, '');
        const isRTL = isRTLText(bulletText);
        elements.push(
          <div key={key++} dir={isRTL ? 'rtl' : 'ltr'} className={`flex items-start mb-1 ml-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className={`${textColor} text-[0.85rem] ${isRTL ? 'ml-2' : 'mr-2'} mt-px`}>◦</span>
            <span dir={isRTL ? 'rtl' : 'ltr'} className={`${textColor} text-[0.85rem] leading-[1.5] flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              {bulletText}
            </span>
          </div>
        );
        continue;
      }

      // Regular paragraph
      const isRTL = isRTLText(line);
      elements.push(
        <p
          key={key++}
          dir={isRTL ? 'rtl' : 'ltr'}
          className={`${textColor} text-[0.95rem] leading-[1.6] mb-1 ${isRTL ? 'text-right' : 'text-left'}`}
        >
          {line}
        </p>
      );
    }

    return elements;
  };

  return <div className="w-full">{parseContent(content)}</div>;
};

export default MessageContentFormatter;
